import React, { useState, useRef, useEffect } from 'react';
import styled, { useTheme } from 'styled-components/macro';
import { Input } from '../../SubComponents/Inputs';
import { Button } from '../../SubComponents/Buttons';
import { Label } from '../../SubComponents/Text';
import PhoneInput from './phone-input';
import useLocalize from '../../Hooks/useLocalize';
import { ButtonWrapper } from './style';

export type CustomerFormInputChangeEvent = {
  target: {
    name: string;
    value: string;
    checkValidity: () => boolean;
  };
};

type InputFieldStatus = {
  firstname: boolean;
  lastname: boolean;
  email: boolean;
  phone: boolean;
  birthDate: boolean;
};

type Customer = {
  firstname?: string | null;
  lastname?: string | null;
  email?: string | null;
  phone?: string | null;
  birthDate?: string;
};

interface CustomerFormProps {
  className?: string;
  customer: Customer;
  onChange: (event: CustomerFormInputChangeEvent, country?: string) => void;
  onSubmit?: () => void;
  onFormValid?: (isValid: boolean) => void;
  mobileButton?: boolean;
  showBirthday?: boolean;
  showButton?: boolean;
  errors: {
    booking?: string;
    customerForm: string;
  };
  enabled?: boolean;
  loading?: boolean;
  shopCountry: string | undefined;
}

let CustomerForm = (props: CustomerFormProps) => {
  const {
    className,
    customer,
    onChange,
    onSubmit,
    onFormValid,
    mobileButton,
    showBirthday,
    showButton = true,
    errors,
    enabled = true,
    loading = false,
    shopCountry,
  } = props;

  const localize = useLocalize();

  const [fieldTouched, setFieldTouched] = useState<InputFieldStatus>({
    firstname: false,
    lastname: false,
    email: false,
    phone: false,
    birthDate: false,
  });
  const [fieldValid, setFieldValid] = useState<InputFieldStatus>({
    firstname: false,
    lastname: false,
    email: false,
    phone: false,
    birthDate: false,
  });

  const [isFormValid, setIsFormValid] = useState(false);
  const [hasFormBeenSynced, setHasFormBeenSynced] = useState(false);
  const theme = useTheme();

  const formRef = useRef<HTMLFormElement | null>(null);
  const formButtonRef = useRef<HTMLButtonElement | null>(null);

  const handleSubmit = (event: React.ChangeEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (onSubmit) onSubmit();
  };

  const handleBlur = (event: React.FocusEvent<HTMLInputElement>) => {
    setFieldTouched({ ...fieldTouched, [event.target.name]: true });
  };

  const handleChange = (event: CustomerFormInputChangeEvent, country?: string) => {
    onChange(event, country);

    setFieldValid({ ...fieldValid, [event.target.name]: event.target.checkValidity() });

    setIsFormValid(formRef.current?.checkValidity() ?? false);
  };

  // Trigger form validation when customer form error is set
  useEffect(() => {
    if (errors.customerForm && formButtonRef.current) formButtonRef.current.click();
  }, [errors.customerForm]);

  useEffect(() => {
    const isCustomerKey = (key: string): key is keyof Customer => {
      return key in customer;
    };

    // Check if form is synced
    if (!hasFormBeenSynced && formRef.current) {
      let isFormInSync = true;
      for (const input of Array.from(formRef.current.elements)) {
        // Check if DOM input is valid
        if (input instanceof HTMLInputElement) {
          if (isCustomerKey(input.name)) {
            if (input.value === customer[input.name] && input.checkValidity()) {
              // Check if field is marked as valid
              if (!fieldValid[input.name]) {
                // Mark form as out of sync
                isFormInSync = false;
                break;
              }
            }
          }
        }
      }

      if (!isFormInSync) {
        // Sync form
        for (const input of Array.from(formRef.current.elements)) {
          // Check if DOM input is valid
          if (input instanceof HTMLInputElement) {
            if (input.checkValidity()) {
              // Mark field as valid
              setFieldValid((fieldValid) => ({
                ...fieldValid,
                [input.name]: true,
              }));
            }
          }
        }

        setIsFormValid(formRef.current.checkValidity());

        setHasFormBeenSynced(true);
      }
    }
  }, [customer, fieldValid, hasFormBeenSynced]);

  useEffect(() => {
    if (typeof onFormValid === 'function') onFormValid(isFormValid);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isFormValid]);

  return (
    <form className={className} onSubmit={handleSubmit} ref={formRef}>
      <Input
        type="text"
        name="firstname"
        error={fieldTouched.firstname && !fieldValid.firstname}
        onChange={handleChange}
        onBlur={handleBlur}
        value={customer.firstname ?? ''}
        placeholder={localize('customer.firstname')}
        required
        m="32px 0 0 25px"
        disabled={!enabled}
        autocomplete="given-name"
        className="flex-[1_1_calc(100%_-_25px)] md:flex-[1_1_calc(50%_-_25px)]"
      />
      <Input
        type="text"
        name="lastname"
        error={fieldTouched.lastname && !fieldValid.lastname}
        onChange={handleChange}
        onBlur={handleBlur}
        value={customer.lastname ?? ''}
        placeholder={localize('customer.lastname')}
        required
        m="32px 0 0 25px"
        disabled={!enabled}
        autocomplete="family-name"
        className="flex-[1_1_calc(100%_-_25px)] md:flex-[1_1_calc(50%_-_25px)]"
      />
      <Input
        type="email"
        name="email"
        error={fieldTouched.email && !fieldValid.email}
        onChange={handleChange}
        onBlur={handleBlur}
        value={customer.email ?? ''}
        placeholder={localize('customer.email')}
        required
        m="32px 0 0 25px"
        disabled={!enabled}
        autocomplete="email"
        className="flex-[1_1_calc(100%_-_25px)] md:flex-[1_1_calc(50%_-_25px)]"
      />
      <PhoneInput
        name="phone"
        error={fieldTouched.phone && !fieldValid.phone}
        onChange={handleChange}
        onBlur={handleBlur}
        value={customer.phone ?? ''}
        placeholder={localize('customer.phone')}
        required
        disabled={!enabled}
        shopCountry={shopCountry}
      />

      {showBirthday && (
        <Input
          type="text"
          name="birthDate"
          error={fieldTouched.birthDate && !fieldValid.birthDate}
          onChange={handleChange}
          onBlur={handleBlur}
          value={customer.birthDate}
          placeholder={`${localize('customer.birthDate')} ${localize(
            'customer.birthDatePattern'
          )}`}
          required
          pattern="\d{4}-\d{2}-\d{2}"
          m="32px 0 0 25px"
          flex="0 1 calc(50% - 25px)"
          disabled={!enabled}
          autocomplete="bday"
        />
      )}

      {errors.booking && (
        <Label error ml="25px" mt="3">
          {errors.booking}
        </Label>
      )}

      {showButton && (
        <ButtonWrapper>
          <Button
            loading={loading && !errors.booking}
            type="submit"
            ref={formButtonRef}
            mobile={mobileButton}
            className={!isFormValid && 'disabled'}
            mt="4"
            look="secondary"
            size={theme.name === 'starboard' ? 'big' : 'default'}
          >
            <span>{localize('product.next')}</span>
          </Button>
        </ButtonWrapper>
      )}
    </form>
  );
};

CustomerForm = styled(CustomerForm)`
  display: flex;
  flex-wrap: wrap;
  /* Negativ margin to remove extra margin */
  margin: -32px 0 0 -25px;
  margin-bottom: 32px;
`;

export default CustomerForm;
